import { useTranslation } from "react-i18next";

/**
 * Route-level Suspense fallback for the lazily-loaded Lobby + Game
 * arms. Renders the SAME outer `<main id="main-content">` landmark
 * the loaded route mounts (Login / Lobby / Game all carry it) so the
 * skip-to-content target stays in the DOM while the route chunk is in
 * flight. The page-level dark base (`body` / `#root` →
 * `--color-bg-base`) shows through, so there is no white flash; the
 * shell class (`lobby-shell` / `game-shell`) reproduces the loaded
 * route's outer layout box (`flex: 1` for game, centered for lobby)
 * so the felt + chrome don't shift when the real route swaps in.
 *
 * `variant` matches the wrapper class the arm's route uses so the
 * fallback's layout box is pixel-identical to the surface that
 * replaces it.
 */
function RouteFallback({
  variant,
}: {
  readonly variant: "lobby" | "game";
}): JSX.Element {
  const { t } = useTranslation();
  const shellClass = variant === "game" ? "game-shell" : "lobby-shell";
  // Inline centering keeps the affordance scope-confined to this
  // file: `.lobby-shell` already centers, but `.game-shell` is a
  // column flow, so the `flex: 1` wrapper here floats the text on
  // the felt's dark base for both arms without a stylesheet edit.
  return (
    <main
      id="main-content"
      className={shellClass}
      aria-busy="true"
      aria-label={t("common.loading")}
    >
      <div
        role="status"
        aria-live="polite"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "8rem",
          opacity: 0.7,
        }}
      >
        {t("common.loading")}
      </div>
    </main>
  );
}

export { RouteFallback };
